import React, { useState } from 'react';
import { Stack } from '@mui/material';
import { useNotification } from '../../hooks/NotificationContext';
import { modifyUserAccountStatus } from '../../modules/users';
import AddUser from './AddUser';
import ActionButton from '../../ui/primitives/ActionButton';
import ConfirmationModal from '../../ui/primitives/ConfirmationModal';

const modalTitleMap = {
  delete: 'Delete Users',
  deactivate: 'Deactivate Users',
  restore: 'Reactivate Users',
};

const UsersTableAction = ({
  fetchUsersData,
  setShowInactive,
  showInactive,
  seletectItems = [],
  currentUser,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openInvite, setOpenInvite] = useState(false);
  const { addNotification } = useNotification();

  const isOrgAdmin = currentUser?.role === 'org-admin';

  const handleOpenModal = (action) => {
    setModalAction(action);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setModalAction(null);
    setShowModal(false);
  };

  const handleConfirm = async () => {
    if (!modalAction || seletectItems.length === 0) return;

    setLoading(true);
    try {
      await modifyUserAccountStatus(seletectItems, modalAction);
      addNotification(
        'success',
        `Users ${
          modalAction === 'delete'
            ? 'deleted permanently'
            : modalAction === 'deactivate'
            ? 'deactivated'
            : 'reactivated'
        }!`
      );
      fetchUsersData();
    } catch (err) {
      addNotification(
        'danger',
        err?.message || `Failed to ${modalAction} users.`
      );
    }
    handleCloseModal();
    setLoading(false);
  };

  const confirmationText = {
    delete: 'This will permanently delete the selected users. This action cannot be undone.',
    deactivate: 'Are you sure you want to deactivate the selected users?',
    restore: 'Are you sure you want to reactivate the selected users?',
  };

  const isButtonDisabled = seletectItems.length === 0;

  const renderActionButtons = () => {
    if (!isOrgAdmin) return null;

    if (showInactive) {
      return (
        <>
          <ActionButton
            intent="secondary"
            label="Delete"
            disabled={isButtonDisabled}
            onClick={() => handleOpenModal('delete')}
            tooltip={isButtonDisabled ? 'Select users to delete' : 'Delete'}
          />
          <ActionButton
            intent="secondary"
            label="Reactivate"
            disabled={isButtonDisabled}
            onClick={() => handleOpenModal('restore')}
            tooltip={isButtonDisabled ? 'Select users to reactivate' : 'Reactivate'}
          />
        </>
      );
    } else {
      return (
        <ActionButton
          intent="secondary"
          label="Deactivate"
          disabled={isButtonDisabled}
          onClick={() => handleOpenModal('deactivate')}
          tooltip={isButtonDisabled ? 'Select users to deactivate' : 'Deactivate'}
        />
      );
    }
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        {!showInactive && (isOrgAdmin || currentUser?.role === 'org-manager') && (
          <ActionButton
            intent="primary"
            label="+ Invite Member"
            tooltip="Invite members to your organization"
            onClick={() => setOpenInvite(true)}
          />
        )}

        {renderActionButtons()}

        <ActionButton
          intent="secondary"
          label={showInactive ? 'Show Active' : 'Show Inactive'}
          tooltip={`Click to show ${showInactive ? 'Active' : 'Inactive'} Users`}
          onClick={() => setShowInactive(!showInactive)}
        />
      </Stack>

      <AddUser
        fetchUsersData={fetchUsersData}
        open={openInvite}
        setOpen={setOpenInvite}
      />

      <ConfirmationModal
        show={showModal}
        handleClose={handleCloseModal}
        handleConfirm={handleConfirm}
        title={modalTitleMap[modalAction] ?? ''}
        body={confirmationText[modalAction]}
        loading={loading}
      />
    </>
  );
};

export default UsersTableAction;